import { useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, TextField, Typography
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mmApi } from '../../services/api'
import { usePortfolioStore } from '../../store/portfolioStore'

interface MMConfig {
  capital: number
  maxDrawdownPct: number
  riskPerTradePct: number
  atrMultiplier: number
}

interface SuggestionResult {
  suggestedSize: number
  method: string
  confidence: number
  circuitBreakerActive: boolean
  note: string | null
}

export default function MMPanel() {
  const qc = useQueryClient()
  const { selectedPortfolioId } = usePortfolioStore()

  // Config state
  const [cfg, setCfg] = useState<MMConfig>({
    capital: 10000,
    maxDrawdownPct: 20,
    riskPerTradePct: 1,
    atrMultiplier: 2,
  })

  // Suggestion inputs
  const [atr, setAtr] = useState('')
  const [price, setPrice] = useState('')
  const [suggestion, setSuggestion] = useState<SuggestionResult | null>(null)

  // ── Load config ──────────────────────────────────────────────────────────
  const { isLoading: cfgLoading } = useQuery({
    queryKey: ['mm-config'],
    queryFn: () => mmApi.getConfig().then((r) => r.data),
    onSuccess: (data: MMConfig) => setCfg(data),
  } as Parameters<typeof useQuery>[0])

  // ── Circuit-breaker status ───────────────────────────────────────────────
  const { data: cbStatus } = useQuery({
    queryKey: ['cb-status', selectedPortfolioId],
    queryFn: () =>
      mmApi.circuitBreakerStatus(selectedPortfolioId!).then((r) => r.data),
    enabled: !!selectedPortfolioId,
    refetchInterval: 30_000,
  })

  // ── Save config ──────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: () => mmApi.saveConfig(cfg).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mm-config'] }),
  })

  // ── Suggest ──────────────────────────────────────────────────────────────
  const suggestMutation = useMutation({
    mutationFn: () =>
      mmApi
        .suggest({
          portfolioId: selectedPortfolioId ?? 'default',
          atr: parseFloat(atr),
          currentPrice: parseFloat(price),
          method: 'ATR_BASED',
        })
        .then((r) => r.data),
    onSuccess: (data: SuggestionResult) => setSuggestion(data),
  })

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Money Management
      </Typography>

      {/* ── Circuit-Breaker Status ── */}
      {cbStatus && (
        <Alert
          severity={cbStatus.active ? 'error' : cbStatus.softAlert ? 'warning' : 'success'}
          sx={{ mb: 3 }}
        >
          {cbStatus.active
            ? `🔴 Circuit-Breaker ACTIVE – drawdown ${Number(cbStatus.currentDrawdownPct).toFixed(2)}%. No new trades!`
            : cbStatus.softAlert
            ? `🟡 Soft alert – drawdown ${Number(cbStatus.currentDrawdownPct).toFixed(2)}% ≥ 5%. Size reduced 50%.`
            : `🟢 Normal – drawdown ${Number(cbStatus.currentDrawdownPct).toFixed(2)}%`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ── Config Card ── */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Configuration</Typography>

              {cfgLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <TextField
                    fullWidth label="Capital ($)" type="number" value={cfg.capital}
                    onChange={(e) => setCfg((p) => ({ ...p, capital: parseFloat(e.target.value) }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth label="Max Drawdown (%)" type="number"
                    value={cfg.maxDrawdownPct}
                    onChange={(e) => setCfg((p) => ({ ...p, maxDrawdownPct: parseFloat(e.target.value) }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth label="Risk per Trade (%)" type="number"
                    value={cfg.riskPerTradePct}
                    onChange={(e) => setCfg((p) => ({ ...p, riskPerTradePct: parseFloat(e.target.value) }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth label="ATR Multiplier" type="number"
                    value={cfg.atrMultiplier}
                    onChange={(e) => setCfg((p) => ({ ...p, atrMultiplier: parseFloat(e.target.value) }))}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                  >
                    {saveMutation.isPending ? 'Saving…' : 'Save Config'}
                  </Button>
                  {saveMutation.isSuccess && (
                    <Chip label="Saved ✓" color="success" size="small" sx={{ ml: 1 }} />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── Position Sizer ── */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Position Size Calculator</Typography>
              <TextField
                fullWidth label="ATR" type="number" value={atr}
                onChange={(e) => setAtr(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth label="Current Price" type="number" value={price}
                onChange={(e) => setPrice(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained" color="secondary"
                onClick={() => suggestMutation.mutate()}
                disabled={suggestMutation.isPending || !atr || !price}
              >
                {suggestMutation.isPending ? <CircularProgress size={20} /> : 'Calculate'}
              </Button>

              {suggestion && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">Suggested Size</Typography>
                  <Typography variant="h4" fontWeight={700} color={suggestion.circuitBreakerActive ? 'error.main' : 'success.main'}>
                    {Number(suggestion.suggestedSize).toFixed(4)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Chip label={`Method: ${suggestion.method}`} size="small" />
                    <Chip label={`Confidence: ${(Number(suggestion.confidence) * 100).toFixed(0)}%`} size="small" />
                    {suggestion.circuitBreakerActive && (
                      <Chip label="CIRCUIT BREAKER" color="error" size="small" />
                    )}
                  </Box>
                  {suggestion.note && (
                    <Alert severity="warning" sx={{ mt: 2 }}>{suggestion.note}</Alert>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
